import * as THREE from "three";
import { defineModel, type Part } from "../../lib";

// Octopus — a dome-headed coral-orange octopus sitting like a little beanbag:
// one big rounded head-dome over a skirt of eight stubby curled legs arranged
// radially around it, each leg a short arc of shrinking blobs with a curled-up
// tip. Big close-set eyes and rosy cheeks on the front of the dome. Mess batch:
// chunky toy proportions, warm saturated colours.
//
// Frame: faces +Z (the eyes), sits on the ground at y = 0 (the legs rest on
// the floor, the dome hovers just above them). ~0.5 m across, ~0.45 m tall.
//
// Rig (flat, each named node about its own pivot):
//   - "head"  : the dome + face. Bobs up and down like breathing.
//   - "leg-0" .. "leg-3" : four of the eight legs (every other one, starting at
//     the front-right and going around) are named so the wiggle can lift them
//     in alternating phases. The other four stay planted for a grounded read.

const CORAL = "#e8734a";       // main hide — warm coral orange
const CORAL_DARK = "#c2552f";  // shading (leg undersides, dome back)
const CREAM = "#f6efe3";       // belly patch + eye whites
const EYE_DARK = "#3b3230";    // pupils + smile (warm dark, not black)
const CHEEK = "#e8a08a";       // rosy cheek dots

const HEAD_R = 0.21;           // dome radius
const HEAD_CY = 0.24;          // dome centre height
const LEG_COUNT = 8;

// One stubby curled leg: three shrinking blobs stepping out and down from
// under the dome, ending in a little tip curled back up — a cinnamon-roll leg.
// `angle` is the direction it points (0 = +Z, the front), measured on the
// ground plane. Named legs get a pivot where they leave the body.
function leg(angle: number, name?: string): Part[] {
  const dx = Math.sin(angle), dz = Math.cos(angle);
  const yaw = angle; // turn each blob so its stretch follows the leg direction
  const seg = (dist: number, y: number, r: number, sy: number, color: string): Part => ({
    geometry: new THREE.SphereGeometry(r, 10, 8),
    color,
    position: [dx * dist, y, dz * dist],
    scale: [1.0, sy, 1.35],
    rotation: [0, yaw, 0],
    name,
  });
  return [
    // Root blob tucked under the dome, mid blob stepping out, then the tip.
    seg(0.13, 0.09, 0.085, 0.9, CORAL),
    seg(0.23, 0.06, 0.07, 0.85, CORAL),
    seg(0.31, 0.045, 0.055, 0.8, CORAL_DARK),
    // The curled-up tip: a small ball raised at the very end of the leg.
    {
      geometry: new THREE.SphereGeometry(0.04, 8, 6),
      color: CORAL_DARK,
      position: [dx * 0.36, 0.085, dz * 0.36],
      name,
    },
  ];
}

// The pivot a named leg swings about: the point where it leaves the body.
function legPivot(angle: number): [number, number, number] {
  return [Math.sin(angle) * 0.12, 0.09, Math.cos(angle) * 0.12];
}

// The four named (wiggling) legs sit at every other radial slot, offset half a
// slot from the front so no leg pokes straight out under the face.
const SLOT = (i: number) => (i + 0.5) * ((Math.PI * 2) / LEG_COUNT);
const WIGGLE_ANGLES = [SLOT(0), SLOT(2), SLOT(4), SLOT(6)];
const PLANT_ANGLES = [SLOT(1), SLOT(3), SLOT(5), SLOT(7)];

export const octopus = defineModel({
  id: "octopus",
  batchId: "mess",
  name: "Octopus",
  tags: ["creature", "mess", "cute", "animated", "sea", "ocean", "octopus", "animal"],
  summary: "A dome-headed coral octopus on eight stubby curled legs, wiggling happily.",
  description:
    "A storybook octopus: one big coral-orange head-dome sitting on a skirt of " +
    "eight stubby curled legs arranged around it, each ending in a little " +
    "rolled-up tip. Big close-set eyes, rosy cheeks and a small smile on the " +
    "front of the dome. The idle bobs the head like breathing; leg-wiggle " +
    "lifts alternating legs in a happy squirm.",
  author: "models",
  footprint: 0.45,
  tiles: { w: 1, h: 1 },
  parts(): Part[] {
    return [
      // ── the head dome ────────────────────────────────────────────────────
      // Main dome: a big sphere squashed a touch, resting over the leg skirt.
      { geometry: new THREE.SphereGeometry(HEAD_R, 14, 12), color: CORAL,
        position: [0, HEAD_CY, 0], scale: [1.0, 1.05, 1.0], name: "head" },
      // Cream face/belly patch on the lower front of the dome.
      { geometry: new THREE.SphereGeometry(HEAD_R * 0.9, 12, 10), color: CREAM,
        position: [0, HEAD_CY - 0.045, 0.05], scale: [0.85, 0.75, 0.95], name: "head" },

      // ── the face (big and close together) ───────────────────────────────
      // Eye whites.
      { geometry: new THREE.SphereGeometry(0.055, 12, 10), color: CREAM,
        position: [-0.075, HEAD_CY + 0.03, 0.175], name: "head" },
      { geometry: new THREE.SphereGeometry(0.055, 12, 10), color: CREAM,
        position: [0.075, HEAD_CY + 0.03, 0.175], name: "head" },
      // Dark pupils, set slightly forward.
      { geometry: new THREE.SphereGeometry(0.028, 10, 8), color: EYE_DARK,
        position: [-0.072, HEAD_CY + 0.03, 0.218], name: "head" },
      { geometry: new THREE.SphereGeometry(0.028, 10, 8), color: EYE_DARK,
        position: [0.072, HEAD_CY + 0.03, 0.218], name: "head" },
      // Tiny cream highlights for sparkle.
      { geometry: new THREE.SphereGeometry(0.01, 8, 6), color: CREAM,
        position: [-0.06, HEAD_CY + 0.043, 0.24], name: "head" },
      { geometry: new THREE.SphereGeometry(0.01, 8, 6), color: CREAM,
        position: [0.084, HEAD_CY + 0.043, 0.24], name: "head" },
      // Rosy cheeks just below and outside the eyes.
      { geometry: new THREE.SphereGeometry(0.026, 8, 6), color: CHEEK,
        position: [-0.115, HEAD_CY - 0.02, 0.165], scale: [1, 0.7, 0.6], name: "head" },
      { geometry: new THREE.SphereGeometry(0.026, 8, 6), color: CHEEK,
        position: [0.115, HEAD_CY - 0.02, 0.165], scale: [1, 0.7, 0.6], name: "head" },
      // A small smile: a thin dark torus sunk into the face, front arc showing.
      { geometry: new THREE.TorusGeometry(0.045, 0.009, 8, 20), color: EYE_DARK,
        position: [0, HEAD_CY - 0.03, 0.185], rotation: [Math.PI / 2 + 0.25, 0, 0], name: "head" },

      // ── the legs ─────────────────────────────────────────────────────────
      // Four named wigglers (every other slot) + four planted fillers between.
      ...leg(WIGGLE_ANGLES[0], "leg-0"),
      ...leg(WIGGLE_ANGLES[1], "leg-1"),
      ...leg(WIGGLE_ANGLES[2], "leg-2"),
      ...leg(WIGGLE_ANGLES[3], "leg-3"),
      ...PLANT_ANGLES.flatMap((a) => leg(a)),
    ];
  },
  animations: [
    // 1. Idle — the dome bobs and squashes gently like slow breathing while
    //    the named legs give the tiniest ripple so the skirt feels alive.
    {
      id: "idle",
      label: "Idle",
      duration: 3.0,
      loop: true,
      tracks: [
        {
          target: "head", pivot: [0, 0.1, 0],
          position: [
            { t: 0, value: [0, 0, 0], ease: "sine" },
            { t: 1.5, value: [0, 0.025, 0], ease: "sine" },
            { t: 3.0, value: [0, 0, 0], ease: "sine" },
          ],
          scale: [
            { t: 0, value: [1, 1, 1], ease: "sine" },
            { t: 1.5, value: [0.97, 1.05, 0.97], ease: "sine" },
            { t: 3.0, value: [1, 1, 1], ease: "sine" },
          ],
        },
        {
          target: "leg-0", pivot: legPivot(WIGGLE_ANGLES[0]),
          rotation: [
            { t: 0, value: [0, 0, 0], ease: "sine" },
            { t: 1.5, value: [-0.08, 0, 0], ease: "sine" },
            { t: 3.0, value: [0, 0, 0], ease: "sine" },
          ],
        },
        {
          target: "leg-2", pivot: legPivot(WIGGLE_ANGLES[2]),
          rotation: [
            { t: 0, value: [-0.08, 0, 0], ease: "sine" },
            { t: 1.5, value: [0, 0, 0], ease: "sine" },
            { t: 3.0, value: [-0.08, 0, 0], ease: "sine" },
          ],
        },
      ],
    },

    // 2. Leg-wiggle — the signature move: alternating legs lift and curl in a
    //    happy squirm (0 and 2 up while 1 and 3 are down, then swapped), the
    //    dome rocking along with the rhythm.
    {
      id: "leg-wiggle",
      label: "Leg wiggle",
      duration: 1.6,
      loop: true,
      tracks: [
        {
          target: "leg-0", pivot: legPivot(WIGGLE_ANGLES[0]),
          position: [
            { t: 0, value: [0, 0, 0], ease: "easeInOut" },
            { t: 0.4, value: [0, 0.05, 0], ease: "easeInOut" },
            { t: 0.8, value: [0, 0, 0], ease: "easeInOut" },
            { t: 1.6, value: [0, 0, 0], ease: "easeInOut" },
          ],
          rotation: [
            { t: 0, value: [0, 0, 0], ease: "easeInOut" },
            { t: 0.4, value: [-0.35, 0, 0], ease: "easeInOut" },
            { t: 0.8, value: [0, 0, 0], ease: "easeInOut" },
            { t: 1.6, value: [0, 0, 0], ease: "easeInOut" },
          ],
        },
        {
          target: "leg-2", pivot: legPivot(WIGGLE_ANGLES[2]),
          position: [
            { t: 0, value: [0, 0, 0], ease: "easeInOut" },
            { t: 0.4, value: [0, 0.05, 0], ease: "easeInOut" },
            { t: 0.8, value: [0, 0, 0], ease: "easeInOut" },
            { t: 1.6, value: [0, 0, 0], ease: "easeInOut" },
          ],
          rotation: [
            { t: 0, value: [0, 0, 0], ease: "easeInOut" },
            { t: 0.4, value: [-0.35, 0, 0], ease: "easeInOut" },
            { t: 0.8, value: [0, 0, 0], ease: "easeInOut" },
            { t: 1.6, value: [0, 0, 0], ease: "easeInOut" },
          ],
        },
        {
          target: "leg-1", pivot: legPivot(WIGGLE_ANGLES[1]),
          position: [
            { t: 0, value: [0, 0, 0], ease: "easeInOut" },
            { t: 0.8, value: [0, 0, 0], ease: "easeInOut" },
            { t: 1.2, value: [0, 0.05, 0], ease: "easeInOut" },
            { t: 1.6, value: [0, 0, 0], ease: "easeInOut" },
          ],
          rotation: [
            { t: 0, value: [0, 0, 0], ease: "easeInOut" },
            { t: 0.8, value: [0, 0, 0], ease: "easeInOut" },
            { t: 1.2, value: [-0.35, 0, 0], ease: "easeInOut" },
            { t: 1.6, value: [0, 0, 0], ease: "easeInOut" },
          ],
        },
        {
          target: "leg-3", pivot: legPivot(WIGGLE_ANGLES[3]),
          position: [
            { t: 0, value: [0, 0, 0], ease: "easeInOut" },
            { t: 0.8, value: [0, 0, 0], ease: "easeInOut" },
            { t: 1.2, value: [0, 0.05, 0], ease: "easeInOut" },
            { t: 1.6, value: [0, 0, 0], ease: "easeInOut" },
          ],
          rotation: [
            { t: 0, value: [0, 0, 0], ease: "easeInOut" },
            { t: 0.8, value: [0, 0, 0], ease: "easeInOut" },
            { t: 1.2, value: [-0.35, 0, 0], ease: "easeInOut" },
            { t: 1.6, value: [0, 0, 0], ease: "easeInOut" },
          ],
        },
        {
          // The dome rocks gently with the squirm.
          target: "head", pivot: [0, 0.1, 0],
          rotation: [
            { t: 0, value: [0, 0, 0.05], ease: "sine" },
            { t: 0.8, value: [0, 0, -0.05], ease: "sine" },
            { t: 1.6, value: [0, 0, 0.05], ease: "sine" },
          ],
        },
      ],
    },
  ],
});
